import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'moment/locale/pt-br';
import {
    Card,
    Container,
    Box,
    Typography,
    Stack,
    TextField,
    Button,
    Chip,
    Paper,
    Avatar,
    Menu,
    MenuItem,
    Checkbox,
    ListItemIcon,
    ListItemText,
    Tooltip
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { AuthContext } from '../../context/Auth/AuthContext';
import { CalendarToolbar, StyledCalendar } from '../../sections/@dashboard/calendar';
import SimpleCalendarFilter from '../../components/SimpleCalendarFilter';

import api from '../../services/api';

import CreateRecurringEvents from '../../helpers/CreateRecurringEvents';
import {
    shouldUseViewBasedFiltering,
    getEffectiveDateRange,
    formatDateRangeForAPI,
} from '../../utils/calendarViewUtils';

import CalendarEventViewPreview from '../../components/CalendarEventViewPreview';
import CreateOrEditCalendarModal from '../../components/CreateOrEditCalendarModal';
import RescheduleTicketPreview from '../../components/RescheduleTicketPreview';
import useResponsive from '../../hooks/useResponsive';
import socket from '../../hooks/useSocket';
import calendarEventContent from '../../utils/calendarEventContent';
import ToastError from '../../toast/error/toastError';
import ToastSuccess from '../../toast/success/toastSuccess';
import CalendarEventNotification from '../../components/CalendarEventNotification';

// ─────────────────────────────────────────────────────────────────────────
// Constantes de módulo — NUNCA podem viver dentro do componente:
// - momentLocalizer e withDragAndDrop retornam novos componentes/objetos a
//   cada chamada. Se criados em render, o react-big-calendar enxerga uma
//   árvore de componentes nova a cada render e remonta tudo.
// - moment.locale é um side-effect global, basta rodar uma vez ao carregar.
// ─────────────────────────────────────────────────────────────────────────

moment.locale('pt-br');
// commit jonatan
const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(BigCalendar);

const EVENT_COLORS = {
    whatsapp: '#25d366',
    schedule: '#ffcc00',
    email: '#cd2030',
    call: '#3923ef',
    reschedule_ticket: 'rgb(47, 82, 255)',
    default: '#3923ef',
};

const CALENDAR_MESSAGES = {
    allDay: 'Dia inteiro',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Lista',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Não há eventos neste período',
    showMore: (total) => `+ ${total} mais`,
};

const CALENDAR_FORMATS = {
    timeGutterFormat: 'HH:mm',
    eventTimeRangeFormat: ({ start, end }, culture, l) =>
        l.format(start, 'HH:mm', culture) + ' - ' + l.format(end, 'HH:mm', culture),
    dayFormat: 'ddd DD/MM',
    dayHeaderFormat: 'dddd, DD [de] MMMM',
    dayRangeHeaderFormat: ({ start, end }, culture, l) =>
        l.format(start, 'DD [de] MMMM', culture) + ' - ' +
        l.format(end, 'DD [de] MMMM [de] YYYY', culture),
};

// Estilo de evento: depende apenas do próprio evento, portanto é puro e
// pode viver fora do componente. Antes era recriado a cada render.
const eventPropGetter = (event) => {
    const color = event.color || EVENT_COLORS[event.type] || EVENT_COLORS.default;

    if (event.isRescheduleTicket) {
        const isExecuted = event.status === 'executed';
        return {
            style: {
                backgroundColor: color,
                borderColor: color,
                color: '#ffffff',
                border: isExecuted
                    ? '2px solid rgba(255, 255, 255, 0.4)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                opacity: isExecuted ? 0.85 : 1,
                boxShadow: isExecuted
                    ? '0 2px 4px rgba(0, 0, 0, 0.15)'
                    : '0 1px 3px rgba(0, 0, 0, 0.12)',
                fontWeight: isExecuted ? 600 : 500,
            },
        };
    }

    return {
        style: {
            backgroundColor: color,
            borderColor: color,
            color: '#ffffff',
            border: '0px',
            opacity: event.isFinished || event.isRescheduled ? 0.6 : 1,
            textDecoration: event.isFinished || event.isRescheduled ? 'line-through' : 'none',
        },
    };
};

// Slots de componentes do BigCalendar — também imutáveis.
const CALENDAR_COMPONENTS = {
    event: ({ event }) => calendarEventContent({ event }),
    toolbar: () => null,
};

const VIEW_UNIT_BY_VIEW = {
    month: 'months',
    week: 'weeks',
    day: 'days',
    agenda: 'weeks',
    resource: 'days',
};

const DEFAULT_RECURRENCE = { ONCE: true, WEEKLY: true, MONTHLY: true, ANNUALLY: true };
const DEFAULT_STATUS = { active: true, finished: false, canceled: false, rescheduled: false };

// Helper de log em desenvolvimento. Evita poluir o console em produção.
const devLog = (...args) => {
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};


const reducer = (state, action) => {
    switch (action.type) {
        // REPLACE substitui todo o estado pelo payload — evita o "flash" de
        // RESET → load assíncrono que dava antes (com setTimeout 300ms).
        case "REPLACE_SCHEDULE_MESSAGES":
            return Array.isArray(action.payload) ? [...action.payload] : [];

        case "LOAD_SCHEDULE_MESSAGES": {
            const scheduleMessages = action.payload || [];
            const existing = new Map(state.map((s) => [s.id, s]));
            scheduleMessages.forEach((s) => existing.set(s.id, s));
            return Array.from(existing.values());
        }

        case "UPDATE_SCHEDULE_MESSAGES": {
            const schedule = action.payload;
            const scheduleIndex = state.findIndex((q) => q.id === schedule.id);
            if (scheduleIndex !== -1) {
                const next = state.slice();
                next[scheduleIndex] = schedule;
                return next;
            }
            return [schedule, ...state];
        }

        case "DELETE_SCHEDULE_MESSAGES":
            return state.filter((q) => q.id !== action.payload);

        case "RESET":
            return [];

        default:
            return state;
    }
};

const rescheduleTicketReducer = (state, action) => {
    switch (action.type) {
        case "REPLACE_RESCHEDULE_TICKETS":
            return Array.isArray(action.payload) ? [...action.payload] : [];

        case "LOAD_RESCHEDULE_TICKETS": {
            const tickets = action.payload || [];
            const existing = new Map(state.map((t) => [t.id, t]));
            tickets.forEach((t) => existing.set(t.id, t));
            return Array.from(existing.values());
        }

        case "UPDATE_RESCHEDULE_TICKET": {
            const ticket = action.payload;
            const idx = state.findIndex((t) => t.id === ticket.id);
            if (idx !== -1) {
                const next = state.slice();
                next[idx] = ticket;
                return next;
            }
            return [ticket, ...state];
        }

        case "DELETE_RESCHEDULE_TICKET":
            return state.filter((t) => t.id !== action.payload);

        case "RESET":
            return [];

        default:
            return state;
    }
};

const Calendar = () => {
    const { user } = useContext(AuthContext);
    const [events, dispatch] = useReducer(reducer, []);
    const [rescheduleTickets, dispatchRescheduleTickets] = useReducer(rescheduleTicketReducer, []);
    // recurringEvents é DERIVADO de events/rescheduleTickets/view/viewRescheduled
    // — não precisa de useState. Antes vivia em state + useEffect que disparava
    // a cada mudança, gerando um render extra. useMemo é puro e síncrono.
    const isDesktop = useResponsive('up', 'sm');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingScheduleMessageId, setDeletingScheduleMessageId] = useState(null);
    const [view, setView] = useState(isDesktop ? 'month' : 'agenda');
    const [resources, setResources] = useState([]);
    const [date, setDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [openFilter, setOpenFilter] = useState(false);
    const [openCalendarModal, setOpenCalendarModal] = useState(false);
    const [openCalendarEventInfo, setOpenCalendarEventInfo] = useState(false);
    const [eventData, setEventData] = useState(null);
    const [openCalendarEditModal, setOpenCalendarEditModal] = useState(false);
    const [calendarId, setCalendarId] = useState(null);
    const [isCalendarReschedule, setIsCalendarReschedule] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [openRescheduleTicketPreview, setOpenRescheduleTicketPreview] = useState(false);
    const [selectedRescheduleTicketId, setSelectedRescheduleTicketId] = useState(null);
    // localizer, DragAndDropCalendar e moment.locale foram hoisteados pro
    // topo do módulo — antes eram recriados a cada render do componente, o
    // que forçava o react-big-calendar a remontar toda a árvore.

    // Filter states (simplified)
    const [availableUsers, setAvailableUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [dateFilters, setDateFilters] = useState({
        startDate: null,
        endDate: null
    });
    const [visibleUsers, setVisibleUsers] = useState([]); // Controla quais usuários aparecem nos resources
    const [recurrenceFilters, setRecurrenceFilters] = useState({
        ONCE: true,
        WEEKLY: true,
        MONTHLY: true,
        ANNUALLY: true
    });
    const [statusFilters, setStatusFilters] = useState({
        active: true,
        finished: false,
        canceled: false,
        rescheduled: false
    });
    const [searchParam, setSearchParam] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Legacy filter
    const [viewRescheduled, setViewRescheduled] = useState(false);

    // View-based date filtering
    const [viewBasedDateRange, setViewBasedDateRange] = useState(null);
    const [isViewBasedFiltering, setIsViewBasedFiltering] = useState(true);

    // Menu state for user selection
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const userMenuOpen = Boolean(userMenuAnchorEl);

    const getEventData = async (eventId) => {
        try {
            const { data } = await api.get(`/calendar/${eventId}`)
            setEventData(data)
            setTimeout(() => { setOpenCalendarEventInfo(true) }, 500);
        } catch (error) {
            console.error(error)
        }
    };

    const handleEventClick = async (event) => {
        // Para react-big-calendar, as propriedades estão diretamente no evento
        const calendarItem = event;

        // Verificar se é um ticket reagendado
        if (calendarItem?.isRescheduleTicket) {
            setSelectedRescheduleTicketId(calendarItem.rescheduleTicketId);
            setOpenRescheduleTicketPreview(true);
            return;
        }

        if (calendarItem?.isFinished) {
            // Extrair ID real (remover sufixo de recorrência se existir)
            const realId = calendarItem.id.toString().includes('_') ?
                calendarItem.id.toString().split('_')[0] : calendarItem.id;
            await getEventData(realId);
            return;
        }
        if (calendarItem?.isRescheduled) return ToastError('Evento remarcado não pode ser editado!');
        setSelectedEvent(event);
        // Extrair ID real (remover sufixo de recorrência se existir)
        const realId = calendarItem.id.toString().includes('_') ?
            calendarItem.id.toString().split('_')[0] : calendarItem.id;
        setCalendarId(realId);
        setOpenCalendarEventInfo(true);
    };

    // Fetch users for filter
    const fetchUsers = async () => {
        try {
            setUsersLoading(true);
            const { data } = await api.get("/users/", { params: { isActive: true, getAllUsers: false } });
            const users = data.users || [];
            setAvailableUsers(users);

            // Tentar carregar usuários selecionados do localStorage
            const savedVisibleUserIds = localStorage.getItem('calendar-visible-users');

            if (savedVisibleUserIds) {
                try {
                    const userIds = JSON.parse(savedVisibleUserIds);
                    // Filtrar apenas usuários que ainda existem na lista atual
                    const savedUsers = users.filter(user => userIds.includes(user.id));
                    if (savedUsers.length > 0) {
                        setVisibleUsers(savedUsers);
                        devLog('📂 Carregou usuários salvos do localStorage:', savedUsers.map(u => u.name));
                    } else {
                        // Se nenhum usuário salvo existe mais, usar todos
                        setVisibleUsers(users);
                    }
                } catch (error) {
                    console.error('Erro ao parsear usuários salvos:', error);
                    setVisibleUsers(users);
                }
            } else {
                // Por padrão, todos os usuários são visíveis
                setVisibleUsers(users);
            }

            // Criar recursos baseados nos usuários visíveis será feito pelo useEffect
        } catch (error) {
            console.error('Error fetching users:', error);
            ToastError('Erro ao carregar usuários');
        } finally {
            setUsersLoading(false);
        }
    };

    // Atualizar recursos baseados nos usuários visíveis
    const updateResources = () => {
        const userResources = visibleUsers.map(user => ({
            resourceId: user.id,
            resourceTitle: user.name,
            user: user
        }));
        setResources(userResources);
    };

    // Fetch calendar events with current filters
    const fetchCalendarEvents = async () => {
        try {
            setLoading(true);
            const params = {};

            // Determine effective date range (manual or view-based)
            const effectiveDateRange = getEffectiveDateRange(dateFilters, view, date);
            const apiDateRange = formatDateRangeForAPI(effectiveDateRange);

            // Apply date range to params
            if (apiDateRange.startDate) {
                params.startDate = apiDateRange.startDate;
            }
            if (apiDateRange.endDate) {
                params.endDate = apiDateRange.endDate;
            }

            // Debug log for development
            devLog('📅 view-date-range:', { view, date, effectiveDateRange });

            // User filter - usar visibleUsers para filtrar eventos por usuário em todas as views
            // Só aplicar filtro se alguns usuários específicos estão selecionados
            if (visibleUsers.length > 0 && visibleUsers.length < availableUsers.length) {
                params.userIds = visibleUsers.map(user => user.id).join(',');
                devLog('🔍 Aplicando filtro de usuários:', params.userIds);
            } else {
                // Se nenhum usuário selecionado OU todos selecionados = mostrar todos os eventos
                devLog('🔍 Sem filtro de usuário aplicado - mostrando todos os eventos');
            }

            if (debouncedSearch.trim()) {
                params.searchParam = debouncedSearch.trim();
            }

            // Recurrence filters - only send if not all are selected
            const activeRecurrenceTypes = Object.entries(recurrenceFilters)
                .filter(([type, active]) => active)
                .map(([type]) => type);
            if (activeRecurrenceTypes.length > 0 && activeRecurrenceTypes.length < 4) {
                params.recurrenceTypes = activeRecurrenceTypes.join(',');
            }

            // Status filters - only send if not default (only active)
            const activeStatusTypes = Object.entries(statusFilters)
                .filter(([status, active]) => active)
                .map(([status]) => status);
            if (activeStatusTypes.length > 0) {
                params.statusTypes = activeStatusTypes.join(',');
            }

            devLog('📅 Fazendo chamada API com parâmetros:', params);
            const { data } = await api.get('/calendar', { params });
            if (data.calendars) {
                // Substitui atomicamente (sem o flash de RESET → reload).
                dispatch({ type: "REPLACE_SCHEDULE_MESSAGES", payload: data.calendars });
            }

            // Update view-based date range state for UI display
            setViewBasedDateRange(effectiveDateRange);
            setIsViewBasedFiltering(shouldUseViewBasedFiltering(dateFilters, view));

        } catch (err) {
            console.error('Erro ao carregar eventos:', err);
            ToastError('Erro ao carregar eventos do calendário');
        } finally {
            setLoading(false);
        }
    };

    // Fetch reschedule tickets
    const fetchRescheduleTickets = async () => {
        try {
            setLoading(true);
            const params = {};

            // Determine effective date range (manual or view-based)
            const effectiveDateRange = getEffectiveDateRange(dateFilters, view, date);
            const apiDateRange = formatDateRangeForAPI(effectiveDateRange);

            // Apply date range to params - converter YYYY-MM-DD para DD-MM-YYYY
            if (apiDateRange.startDate) {
                params.dateFrom = moment(apiDateRange.startDate, 'YYYY-MM-DD').format('DD-MM-YYYY');
            }
            if (apiDateRange.endDate) {
                params.dateTo = moment(apiDateRange.endDate, 'YYYY-MM-DD').format('DD-MM-YYYY');
            }

            // User filter
            if (visibleUsers.length > 0 && visibleUsers.length < availableUsers.length) {
                params.userId = visibleUsers.map(user => user.id).join(',');
                devLog('🔍 Aplicando filtro de usuários para tickets reagendados:', params.userId);
            }

            if (debouncedSearch.trim()) {
                params.searchParam = debouncedSearch.trim();
            }

            devLog('📅 Buscando tickets reagendados com parâmetros:', params);
            const { data } = await api.get('/reschedule-tickets', { params });

            devLog('📥 Resposta da API de tickets reagendados:', {
                total: data.rescheduleTickets?.length || 0,
                tickets: data.rescheduleTickets
            });

            if (data.rescheduleTickets) {
                // Substitui atomicamente (sem o flash de RESET → reload).
                dispatchRescheduleTickets({
                    type: "REPLACE_RESCHEDULE_TICKETS",
                    payload: data.rescheduleTickets,
                });
            }
        } catch (err) {
            console.error('Erro ao carregar tickets reagendados:', err);
            ToastError('Erro ao carregar tickets reagendados');
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    // Carrega usuários uma única vez ao montar — não depende de filtros.
    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetcha eventos e tickets reagendados quando QUALQUER input que afeta a
    // query muda (datas, filtros, busca debounced, view/date de navegação).
    // Antes eram 3 useEffects separados que disparavam o mesmo par de fetches —
    // no mount fazia 2x e em mudanças de view+date podia disparar dupla.
    // Centralizar aqui também elimina race conditions entre os efeitos.
    useEffect(() => {
        fetchCalendarEvents();
        fetchRescheduleTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFilters, visibleUsers, debouncedSearch, recurrenceFilters, statusFilters, view, date]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchParam);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchParam]);

    // Computa eventos para o BigCalendar a partir do estado bruto. Aplica:
    //  1. Expansão de recorrências (semanais/mensais/anuais virando ocorrências)
    //  2. Filtro legacy de remarcados
    //  3. Conversão de rescheduleTickets em eventos do calendário
    //  4. Mapeamento resourceId para a view de recursos (1 evento por user)
    const recurringEvents = useMemo(() => {
        let calculated = events.flatMap((item) =>
            CreateRecurringEvents(item, view !== 'month')
        );

        if (!viewRescheduled) {
            calculated = calculated.filter(
                (e) => e?.extendedProps?.isRescheduled !== true
            );
        }

        const rescheduleEvents = rescheduleTickets.map((ticket) => {
            const startDateTime = moment(
                `${ticket.rescheduleDateAt} ${ticket.rescheduleTimeAt}`,
                'DD-MM-YYYY HH:mm'
            );
            const endDateTime = startDateTime.clone().add(1, 'hour');
            const statusColor = ticket.status === 'executed' ? '#4CAF50' : 'rgb(47, 82, 255)';

            return {
                id: `reschedule_${ticket.id}`,
                title: ticket.contact?.name || 'Ticket Reagendado',
                start: startDateTime.toDate(),
                end: endDateTime.toDate(),
                type: 'reschedule_ticket',
                color: statusColor,
                isRescheduleTicket: true,
                rescheduleTicketId: ticket.id,
                ticketId: ticket.ticketId,
                contact: ticket.contact,
                user: ticket.user,
                queue: ticket.queue,
                status: ticket.status,
                users: ticket.user ? [ticket.user] : [],
            };
        });

        let allEvents = [...calculated, ...rescheduleEvents];

        if (view === 'resource') {
            allEvents = allEvents
                .map((event) => {
                    const firstUser = event.users?.[0] || null;
                    return { ...event, resourceId: firstUser ? firstUser.id : null };
                })
                .filter((event) => event.resourceId);
        }

        return allEvents;
    }, [events, rescheduleTickets, view, viewRescheduled]);



    useEffect(() => {
        if (!user?.companyId) return;

        const companyId = user.companyId;

        socket.on("calendar", (data) => {
            if (data.action === "update") {
                dispatch({ type: "UPDATE_SCHEDULE_MESSAGES", payload: data.calendar });
            }
            if (data.action === "create") {
                dispatch({ type: "LOAD_SCHEDULE_MESSAGES", payload: [data.calendar] });
            }
            if (data.action === "delete") {
                dispatch({ type: "DELETE_CONTACT", payload: +data.calendarId });
            }
        });

        socket.on(`rescheduleTicket-${companyId}`, (data) => {
            if (data.action === "update") {
                dispatchRescheduleTickets({ type: "UPDATE_RESCHEDULE_TICKET", payload: data.rescheduleTicket });
            }
            if (data.action === "create") {
                dispatchRescheduleTickets({ type: "LOAD_RESCHEDULE_TICKETS", payload: [data.rescheduleTicket] });
            }
            if (data.action === "delete") {
                dispatchRescheduleTickets({ type: "DELETE_RESCHEDULE_TICKET", payload: data.rescheduleTicketId });
            }
            if (data.action === "cancel") {
                fetchRescheduleTickets(); // Refetch para remover o ticket cancelado
            }
        });

        return () => {
            socket.off("calendar");
            socket.off(`rescheduleTicket-${companyId}`);
        };
    }, [user?.companyId]);

    // Atualizar resources quando os usuários visíveis mudarem
    useEffect(() => {
        updateResources();
    }, [visibleUsers]);

    // Salvar usuários visíveis no localStorage sempre que mudarem
    useEffect(() => {
        if (visibleUsers.length > 0 && availableUsers.length > 0) {
            const userIds = visibleUsers.map(user => user.id);
            localStorage.setItem('calendar-visible-users', JSON.stringify(userIds));
            devLog('💾 Salvou usuários no localStorage:', visibleUsers.map(u => u.name));
        }
    }, [visibleUsers, availableUsers]);

    const handleDeleteScheduleMessage = () => {
        dispatch({
            type: "DELETE_SCHEDULE_MESSAGES",
            payload: deletingScheduleMessageId
        });
        setConfirmModalOpen(false);
    };

    const handleEventDateChange = async (data) => {
        console.info(data)
        const { event, start, end, isResize = false } = data;

        const startTime = moment(start).format('HH:mm:ss');
        const endTime = moment(end).format('HH:mm:ss');

        await api.put(`/calendar/${event.id}`, { startAt: start, endAt: end, startTime, endTime });

        dispatch({
            type: "UPDATE_SCHEDULE_MESSAGES",
            payload: {
                ...event,
                start: start,
                end: end,
            },
        });
    };

    const handleResizeEvent = async (data) => {
        // Verificar se é um ticket reagendado
        if (data.event?.isRescheduleTicket) {
            ToastError('Não é possível alterar a data ou horário de um reagendamento de ticket!');
            return;
        }
        await handleEventDateChange({ ...data, isResize: true });
    };

    const handleDropEvent = async (data) => {
        // Verificar se é um ticket reagendado
        if (data.event?.isRescheduleTicket) {
            ToastError('Não é possível alterar a data ou horário de um reagendamento de ticket!');
            return;
        }
        await handleEventDateChange({ ...data, isResize: false });
    };

    const handleCreateUpdateEvent = (newEvent) => {
        if (newEvent.id) {
            dispatch({ type: "UPDATE_SCHEDULE_MESSAGES", payload: newEvent });
            // ToastSuccess('Update success!');
        } else {
            dispatch({ type: "LOAD_SCHEDULE_MESSAGES", payload: [newEvent] });
            // ToastSuccess('Create success!');
        }
    };

    const handleDeleteEvent = async (id) => {
        if (id) {
            await api.delete(`/calendar/${id}`);
            dispatch({ type: "DELETE_SCHEDULE_MESSAGES", payload: id });
            setSelectedEvent(null);
            setOpenCalendarEventInfo(false);
            setIsCalendarReschedule(false);
            ToastSuccess('O evento foi cancelado com sucesso!');
        }
    };

    const handleFinishEvent = async (id) => {
        if (id) {
            const update = await api.put(`/calendar/${id}`, { isFinished: true });
            dispatch({ type: "UPDATE_SCHEDULE_MESSAGES", payload: update });
            setSelectedEvent(null);
            setOpenCalendarEventInfo(false);
            setIsCalendarReschedule(false);
            ToastSuccess('O evento foi finalizado com sucesso!');
        }
    };

    const handleClickDateNext = () => {
        const newDate = moment(date).add(1, getViewUnit()).toDate();
        setDate(newDate);
        devLog('📅 Navegação: Próximo →', newDate);
    };

    const handleClickDatePrev = () => {
        const newDate = moment(date).subtract(1, getViewUnit()).toDate();
        setDate(newDate);
        devLog('📅 Navegação: Anterior ←', newDate);
    };

    const handleClickToday = () => {
        const newDate = new Date();
        setDate(newDate);
        devLog('📅 Navegação: Hoje', newDate);
    };

    const handleChangeView = (newView) => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            setView(newView);
            devLog('📅 Mudança de view:', newView, date);
        }, 100);
    };

    // Lookup hoisteado (VIEW_UNIT_BY_VIEW). Mantemos o helper local pra não
    // expor 'view' à closure de cada handler de navegação.
    const getViewUnit = () => VIEW_UNIT_BY_VIEW[view] || 'months';

    const handleNavigate = (newDate, navigate, view) => {
        // Esta função é chamada automaticamente pelo BigCalendar
        // mas vamos manter o controle através do CalendarToolbar
        setDate(newDate);
        devLog('📅 Navegação pelo BigCalendar:', navigate, newDate);
    };

    // eventPropGetter foi hoisteado pro topo do módulo — antes era recriado
    // a cada render e fazia o react-big-calendar reaplicar estilos em todos
    // os eventos. A função é pura (depende só do evento).

    // Handler para quando clica em um slot vazio (dia ou horário)
    const handleSelectSlot = (slotInfo) => {
        devLog('📅 Slot selecionado:', slotInfo);

        // Verificar se é uma seleção de múltiplos dias
        const isMultiDaySelection = slotInfo.start.getDate() !== slotInfo.end.getDate() ||
            slotInfo.start.getMonth() !== slotInfo.end.getMonth() ||
            slotInfo.start.getFullYear() !== slotInfo.end.getFullYear();

        // Na view resource, encontrar o usuário correspondente ao resourceId
        let selectedUser = null;
        if (view === 'resource' && slotInfo.resourceId) {
            selectedUser = availableUsers.find(user => user.id === slotInfo.resourceId);
        }

        // Preparar dados do slot para o modal
        const selectedSlotData = {
            start: slotInfo.start,
            end: slotInfo.end,
            action: slotInfo.action, // 'select', 'click', 'doubleClick'
            bounds: slotInfo.bounds,
            box: slotInfo.box,
            isMultiDay: isMultiDaySelection,
            slots: slotInfo.slots || [], // Array de slots selecionados
            resourceId: slotInfo.resourceId,
            selectedUser: selectedUser // Usuário pré-selecionado na view resource
        };

        devLog('📅 Dados do slot processados:', {
            isMultiDay: isMultiDaySelection,
            startDate: slotInfo.start.toLocaleDateString(),
            endDate: slotInfo.end.toLocaleDateString(),
            startTime: slotInfo.start.toLocaleTimeString(),
            endTime: slotInfo.end.toLocaleTimeString(),
            resourceId: slotInfo.resourceId,
            selectedUser: selectedUser
        });

        setSelectedSlot(selectedSlotData);
        setOpenCalendarModal(true);
    };

    const handleOpenFilter = () => {
        setOpenFilter(true);
    };

    const handleCloseFilter = () => {
        setOpenFilter(false);
    };

    // Filter handlers
    const handleDateFilterChange = (field, value) => {
        try {
            setDateFilters(prev => ({
                ...prev,
                [field]: value
            }));
        } catch (error) {
            console.error('Error setting date filter:', error);
        }
    };

    const handleResourceVisibilityChange = (event, newValues) => {
        setVisibleUsers(newValues || []);
    };

    const handleUserMenuClick = (event) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleUserToggle = (user) => {
        const isSelected = visibleUsers.some(u => u.id === user.id);
        if (isSelected) {
            const newVisibleUsers = visibleUsers.filter(u => u.id !== user.id);
            devLog('👤 Removendo usuário:', user.name, 'Nova lista:', newVisibleUsers.map(u => u.name));
            setVisibleUsers(newVisibleUsers);
        } else {
            const newVisibleUsers = [...visibleUsers, user];
            devLog('👤 Adicionando usuário:', user.name, 'Nova lista:', newVisibleUsers.map(u => u.name));
            setVisibleUsers(newVisibleUsers);
        }
    };

    const handleSelectAllUsers = () => {
        if (visibleUsers.length === availableUsers.length) {
            // Se todos estão selecionados, deselecionar todos
            setVisibleUsers([]);
            devLog('👥 Desmarcou todos os usuários');
        } else {
            // Se nem todos estão selecionados, selecionar todos
            setVisibleUsers(availableUsers);
            devLog('👥 Selecionou todos os usuários');
        }
    };

    const handleRecurrenceFilterChange = (type, checked) => {
        setRecurrenceFilters(prev => ({
            ...prev,
            [type]: checked
        }));
    };

    const handleStatusFilterChange = (status, checked) => {
        setStatusFilters(prev => ({
            ...prev,
            [status]: checked
        }));
    };

    const handleSearchChange = (value) => {
        setSearchParam(value);
    };

    const resetFilters = () => {
        setDateFilters({ startDate: null, endDate: null });
        setVisibleUsers(availableUsers); // Resetar para mostrar todos os usuários
        setRecurrenceFilters({ ONCE: true, WEEKLY: true, MONTHLY: true, ANNUALLY: true });
        setStatusFilters({ active: true, finished: false, canceled: false, rescheduled: false });
        setSearchParam('');
        setDebouncedSearch('');

        // Limpar localStorage quando resetar filtros
        localStorage.removeItem('calendar-visible-users');
        devLog('🧹 Filtros resetados - voltando para filtro automático baseado na view');
        devLog('🗑️ Removeu usuários salvos do localStorage');
    };

    const hasActiveFilters = () => {
        // Manual date filters (not view-based)
        const hasManualDateFilter = dateFilters.startDate || dateFilters.endDate;
        // Só considera filtro ativo se alguns usuários específicos estão selecionados (não todos nem nenhum)
        const hasUserFilter = visibleUsers.length > 0 && visibleUsers.length < availableUsers.length;
        const hasRecurrenceFilter = !Object.values(recurrenceFilters).every(v => v);
        const hasStatusFilter = statusFilters.finished || statusFilters.canceled || statusFilters.rescheduled || !statusFilters.active;
        const hasSearch = searchParam.length > 0;

        // Only consider manual filters as "active" (view-based is automatic)
        return hasManualDateFilter || hasUserFilter || hasRecurrenceFilter || hasStatusFilter || hasSearch;
    };


    return (
        <Container maxWidth={false}>
            {/* Filtros no Topo */}
            <Paper
                elevation={1}
                sx={{
                    p: 2,
                    mb: 3,
                    mt: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        Filtros do Calendário
                    </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                    {/* Busca */}
                    <TextField
                        placeholder="Buscar eventos..."
                        value={searchParam}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        size="small"
                        sx={{ minWidth: 200, flexGrow: 1 }}
                    />

                    {/* Filtro de Usuários Visíveis (Resource) */}
                    <Box sx={{ minWidth: 250 }}>
                        <Tooltip
                            title={
                                visibleUsers.length > 0 ? (
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Usuários selecionados ({visibleUsers.length}):
                                        </Typography>
                                        {visibleUsers.map((user, index) => (
                                            <Typography key={user.id} variant="body2" sx={{ mb: 0.5 }}>
                                                • {user.name || `Usuário ${user.id}`}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : 'Nenhum usuário selecionado'
                            }
                            arrow
                            placement="top"
                            enterDelay={500}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleUserMenuClick}
                                sx={{
                                    padding: '7px',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    textTransform: 'none',
                                    color: 'text.primary',
                                    borderColor: 'divider'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">
                                        Filtro de Usuários
                                    </Typography>
                                    {visibleUsers.length > 0 ? (
                                        <Chip
                                            size="small"
                                            label={visibleUsers.length <= 3 ?
                                                visibleUsers.map(u => u.name).join(', ') :
                                                `${visibleUsers.slice(0, 3).map(u => u.name).join(', ')} +${visibleUsers.length - 3}`
                                            }
                                            sx={{ maxWidth: 200 }}
                                        />
                                    ) : (
                                        <Chip
                                            size="small"
                                            label="Todos os eventos"
                                            color="info"
                                            variant="outlined"
                                            sx={{ maxWidth: 200 }}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2">▼</Typography>
                            </Button>
                        </Tooltip>

                        <Menu
                            anchorEl={userMenuAnchorEl}
                            open={userMenuOpen}
                            onClose={handleUserMenuClose}
                            slotProps={{
                                paper: {
                                    sx: {
                                        maxHeight: 300,
                                        width: '300px',
                                        mt: 0.5,
                                    },
                                },
                            }}
                        >
                            <MenuItem onClick={handleSelectAllUsers} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                <ListItemIcon>
                                    <Checkbox
                                        checked={visibleUsers.length === availableUsers.length}
                                        indeterminate={visibleUsers.length > 0 && visibleUsers.length < availableUsers.length}
                                    />
                                </ListItemIcon>
                                <ListItemText primary="Selecionar todos" />
                            </MenuItem>

                            {availableUsers.map((user) => {
                                const isSelected = visibleUsers.some(u => u.id === user.id);
                                return (
                                    <MenuItem key={user.id} onClick={() => handleUserToggle(user)}>
                                        <ListItemIcon>
                                            <Checkbox checked={isSelected} />
                                        </ListItemIcon>
                                        <Avatar
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                mr: 1,
                                                fontSize: '0.75rem'
                                            }}
                                            src={user.profileImage}
                                        >
                                            {user.name?.charAt(0)?.toUpperCase()}
                                        </Avatar>
                                        <ListItemText primary={user.name || `Usuário ${user.id}`} />
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </Box>

                    {/* Data Inicial 
                    <TextField
                        label="Data inicial"
                        type="date"
                        size="small"
                        value={dateFilters.startDate ? dateFilters.startDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                            handleDateFilterChange('startDate', date);
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                    />
                    */}
                    {/* Data Final 
                    <TextField
                        label="Data final"
                        type="date"
                        size="small"
                        value={dateFilters.endDate ? dateFilters.endDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value + 'T23:59:59') : null;
                            handleDateFilterChange('endDate', date);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            min: dateFilters.startDate ? dateFilters.startDate.toISOString().split('T')[0] : undefined
                        }}
                        sx={{ minWidth: 150 }}
                    />
                    */}
                    {/* Botões */}
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={resetFilters}
                            disabled={!hasActiveFilters()}
                        >
                            Limpar
                        </Button>
                        {/*
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleOpenFilter}
                        >
                            Mais Filtros
                        </Button>
                        */}
                    </Stack>
                </Stack>

                {/* Resumo dos Filtros Ativos */}
                {hasActiveFilters() && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Filtros ativos:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {dateFilters.startDate && (
                                <Chip
                                    size="small"
                                    label={`Desde: ${(() => {
                                        try {
                                            return dateFilters.startDate.toLocaleDateString('pt-BR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            });
                                        } catch {
                                            return dateFilters.startDate.toISOString().split('T')[0];
                                        }
                                    })()}`}
                                    onDelete={() => handleDateFilterChange('startDate', null)}
                                />
                            )}
                            {dateFilters.endDate && (
                                <Chip
                                    size="small"
                                    label={`Até: ${(() => {
                                        try {
                                            return dateFilters.endDate.toLocaleDateString('pt-BR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            });
                                        } catch {
                                            return dateFilters.endDate.toISOString().split('T')[0];
                                        }
                                    })()}`}
                                    onDelete={() => handleDateFilterChange('endDate', null)}
                                />
                            )}
                            {searchParam && (
                                <Chip
                                    size="small"
                                    label={`Busca: "${searchParam}"`}
                                    onDelete={() => handleSearchChange('')}
                                />
                            )}
                            {visibleUsers.length > 0 && visibleUsers.length < availableUsers.length && (
                                <Chip
                                    size="small"
                                    label={`Usuários: ${visibleUsers.length}/${availableUsers.length} selecionados`}
                                    onDelete={() => setVisibleUsers(availableUsers)}
                                />
                            )}
                            {visibleUsers.length === 0 && (
                                <Chip
                                    size="small"
                                    label="Exibindo todos os eventos (nenhum usuário filtrado)"
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                            {!Object.values(recurrenceFilters).every(v => v) && (
                                <Chip
                                    size="small"
                                    label={`Recorrência: ${Object.entries(recurrenceFilters).filter(([k, v]) => v).map(([k]) => k === 'ONCE' ? 'Único' : k === 'WEEKLY' ? 'Semanal' : k === 'MONTHLY' ? 'Mensal' : 'Anual').join(', ')}`}
                                    onDelete={() => setRecurrenceFilters({ ONCE: true, WEEKLY: true, MONTHLY: true, ANNUALLY: true })}
                                />
                            )}
                            {(statusFilters.finished || statusFilters.canceled || statusFilters.rescheduled || !statusFilters.active) && (
                                <Chip
                                    size="small"
                                    label={`Status: ${Object.entries(statusFilters).filter(([k, v]) => v).map(([k]) => k === 'active' ? 'Ativo' : k === 'finished' ? 'Finalizado' : k === 'canceled' ? 'Cancelado' : 'Remarcado').join(', ')}`}
                                    onDelete={() => setStatusFilters({ active: true, finished: false, canceled: false, rescheduled: false })}
                                />
                            )}
                        </Stack>
                    </Box>
                )}
            </Paper>

            {/* Modals */}
            <CreateOrEditCalendarModal
                open={openCalendarEditModal || openCalendarModal}
                onClose={() => {
                    setOpenCalendarEditModal(false);
                    setOpenCalendarModal(false);
                    setCalendarId(null);
                    setIsCalendarReschedule(false);
                    setSelectedSlot(null);
                }}
                isReschedule={isCalendarReschedule}
                calendarId={calendarId}
                selectedSlot={selectedSlot}
                onConfirm={console.info}
            />

            <CalendarEventNotification
                event={eventData}
                open={openCalendarEventInfo}
                onClose={() => {
                    setEventData(null);
                    setOpenCalendarEventInfo(false);
                }}
                onConfirm={console.info}
            />

            <CalendarEventViewPreview
                open={openCalendarEventInfo}
                onClose={() => {
                    setOpenCalendarEventInfo(false);
                    setCalendarId(null);
                    setIsCalendarReschedule(false);
                }}
                eventId={calendarId}
                onEdit={(id) => {
                    setOpenCalendarEventInfo(false);
                    setCalendarId(id);
                    setIsCalendarReschedule(false);
                    setOpenCalendarEditModal(true);
                }}
                onReschedule={(id) => {
                    console.info({ id });
                    setOpenCalendarEventInfo(false);
                    setIsCalendarReschedule(true);
                    setCalendarId(id);
                    setOpenCalendarEditModal(true);
                }}
                onCancel={(id) => { handleDeleteEvent(id) }}
                onFinish={(id) => { handleFinishEvent(id) }}
            />

            {/* Reschedule Ticket Preview */}
            <RescheduleTicketPreview
                open={openRescheduleTicketPreview}
                onClose={() => {
                    setOpenRescheduleTicketPreview(false);
                    setSelectedRescheduleTicketId(null);
                }}
                rescheduleTicketId={selectedRescheduleTicketId}
            />

            {/* Filter Drawer */}
            <SimpleCalendarFilter
                open={openFilter}
                onClose={handleCloseFilter}
                recurrenceFilters={recurrenceFilters}
                onRecurrenceFilterChange={handleRecurrenceFilterChange}
                statusFilters={statusFilters}
                onStatusFilterChange={handleStatusFilterChange}
                onResetFilter={resetFilters}
            />

            {/* Calendar Card */}
            <Card>
                <StyledCalendar>
                    <CalendarToolbar
                        date={date}
                        view={view}
                        onNextDate={handleClickDateNext}
                        onPrevDate={handleClickDatePrev}
                        onToday={handleClickToday}
                        onChangeView={handleChangeView}
                        onOpenFilter={handleOpenFilter}
                        setOpenCalendarModal={() => {
                            setSelectedSlot(null); // Limpar slot selecionado quando abrir via botão
                            setOpenCalendarModal(true);
                        }}
                    />

                    <DragAndDropCalendar
                        localizer={localizer}
                        events={recurringEvents}
                        startAccessor="start"
                        endAccessor="end"
                        titleAccessor="title"
                        onSelectEvent={handleEventClick}
                        onSelectSlot={handleSelectSlot}
                        onEventDrop={handleDropEvent}
                        onEventResize={handleResizeEvent}
                        onNavigate={handleNavigate}
                        view={view === 'resource' ? 'day' : view}
                        onView={handleChangeView}
                        date={date}
                        resizable={view !== 'month'}
                        selectable={true}
                        selectMultiple={true}
                        allowMultipleSelection={true}
                        longPressThreshold={250}
                        style={{ height: isDesktop ? 720 : 500 }}
                        toolbar={false}
                        eventPropGetter={eventPropGetter}
                        resources={view === 'resource' ? resources : undefined}
                        resourceIdAccessor="resourceId"
                        resourceTitleAccessor="resourceTitle"
                        components={CALENDAR_COMPONENTS}
                        messages={CALENDAR_MESSAGES}
                        formats={CALENDAR_FORMATS}
                    />
                </StyledCalendar>
            </Card>
        </Container>
    );
};

export default Calendar;
